/*
 * Confusion review panel. It scans the selection for Greek confusion cases and
 * reviews them one at a time; Karl can answer the current finding, every
 * finding of its rule, or every finding in the scan. Verdicts, filters, the
 * bulk apply of Sure verdicts and the per-finding undo all sit beside the card
 * so the narrow task pane never needs a second screen.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Button, MessageBar, MessageBarBody, Spinner, Text } from "@fluentui/react-components";
import { useStore } from "../../state/store";
import { useTranslation } from "../../i18n/useTranslation";
import { resolvePlanAwareError } from "../../api/planErrors";
import { UpgradePlanButton } from "../common/UpgradePlanButton";
import { getFriendlyRule, getSelectedConfusionRuleIds } from "../../rules/ruleLogic";
import { readSelectionText } from "../../office/document";
import { replaceOccurrenceInSelection } from "../../office/selection";
import {
  acceptKarlConsent,
  answerKarlVerdicts,
  estimateKarlVerdicts,
  fetchKarlAssists,
  fetchKarlVerdict,
  recordKarlDecisions,
  scanGreekEditorConfusion,
} from "../../api/client";
import {
  OccurrenceChangedError,
  buildKarlCandidates,
  buildKarlDecision,
  buildKarlFinding,
  buildKarlOverview,
  buildKarlSureApplyPlan,
  caseStatusFromBatchResult,
  classifyKarlError,
  countKarlVisibleCases,
  formatKarlBatchSummary,
  formatKarlEstimate,
  karlCaseKey,
  nextKarlVisibleIndex,
  replaceOccurrenceText,
  undoOccurrenceText,
  type KarlCandidate,
} from "../../karl/karlLogic";
import { SuggestionCard } from "../common/SuggestionCard";
import { CollapsibleSection } from "../common/CollapsibleSection";
import { useFindingsAutoCollapse } from "../common/useFindingsAutoCollapse";
import { hasSelectionFindings } from "../../state/findings";
import { karlAvatarStateForConfidence, type KarlAvatarState } from "../../karl/karlAvatar";
import { KarlAvatar } from "./KarlAvatar";
import { KarlConsentModal, type KarlConsentFailure } from "./KarlConsentModal";
import { KarlOverview } from "./KarlOverview";
import { KarlVerdictBadge } from "./KarlVerdictBadge";
import type {
  CatalogSections,
  KarlCaseStatus,
  KarlDecisionInput,
  KarlVerdictBatchEstimate,
  KarlVerdictBatchSummary,
  Lang,
  Occurrence,
  Rule,
} from "../../state/types";

type KarlBulkScope = { kind: "rule"; ruleId: string } | { kind: "all" };

interface KarlBulkState {
  scope: KarlBulkScope;
  phase: "estimating" | "ready" | "running" | "error";
  items: KarlCandidate[];
  estimate: KarlVerdictBatchEstimate | null;
}

type PendingConsentAction = { kind: "ask"; index: number } | { kind: "bulk" };

const withoutKey = <T,>(record: Record<string, T>, key: string): Record<string, T> => {
  const next = { ...record };
  delete next[key];
  return next;
};

const ruleNameFor = (
  ruleId: string,
  rules: Rule[],
  sections: CatalogSections,
  lang: Lang
): string => getFriendlyRule(ruleId, rules, sections, lang).name;

export function SelectionPanel(): JSX.Element {
  const { t, lang, format } = useTranslation();
  const patch = useStore((s) => s.patch);
  const token = useStore((s) => s.token);
  const rules = useStore((s) => s.rules);
  const catalogSections = useStore((s) => s.catalogSections);
  const selectedConfusionRuleIds = useStore((s) => s.selectedConfusionRuleIds);
  const occurrences = useStore((s) => s.occurrences);
  const currentIndex = useStore((s) => s.currentOccurrenceIndex);
  const workingText = useStore((s) => s.workingSelectionText);
  const occurrenceEdits = useStore((s) => s.occurrenceEdits);
  const karlCapability = useStore((s) => s.karlCapability);
  const karlVerdicts = useStore((s) => s.karlVerdicts);
  const karlCaseStatuses = useStore((s) => s.karlCaseStatuses);
  const karlReviewed = useStore((s) => s.karlReviewed);
  const karlAccepted = useStore((s) => s.karlAccepted);
  const karlAssists = useStore((s) => s.karlAssists);

  const karlEnabled = Boolean(karlCapability?.enabled);
  const supportedRuleIds = useMemo(
    () => new Set(karlCapability?.supportedRuleIds ?? []),
    [karlCapability]
  );

  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [skipSure, setSkipSure] = useState(false);
  const [bulk, setBulk] = useState<KarlBulkState | null>(null);
  const [bulkSummary, setBulkSummary] = useState<KarlVerdictBatchSummary | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentFailure, setConsentFailure] = useState<KarlConsentFailure>(null);
  const pendingConsent = useRef<PendingConsentAction | null>(null);
  const scanGeneration = useRef(0);
  const bulkGeneration = useRef(0);

  const numberFormat = useMemo(() => new Intl.NumberFormat(), []);

  const presentError = (error: unknown) => {
    const presentation = resolvePlanAwareError(error, {
      quota: t.quotaExceededMessage,
      forbidden: t.serviceForbiddenMessage,
      fallback: t.statusError,
    });
    setIsError(true);
    setShowUpgrade(presentation.showUpgrade);
    setMessage(`${t.statusError} ${presentation.message}`);
  };

  const presentReviewError = (error: unknown) => {
    if (error instanceof OccurrenceChangedError) {
      setIsError(true);
      setShowUpgrade(false);
      setMessage(`${t.statusError} ${t.occurrenceNotFound}`);
      return;
    }
    presentError(error);
  };

  const setCaseStatus = (index: number, status: KarlCaseStatus | null) => {
    const next = { ...useStore.getState().karlCaseStatuses };
    const key = karlCaseKey(index);
    if (status) next[key] = status;
    else delete next[key];
    patch({ karlCaseStatuses: next });
  };

  const recordDecisions = (decisions: Array<KarlDecisionInput | null>) => {
    const payload = decisions.filter(
      (decision): decision is KarlDecisionInput => decision !== null
    );
    if (!karlEnabled || payload.length === 0) return;
    /* Analytics, not review state: a failed record never undoes a decision. */
    void recordKarlDecisions(payload).catch(() => {});
  };

  const refreshAssists = () => {
    void fetchKarlAssists()
      .then((assists) => {
        if (assists) patch({ karlAssists: assists });
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!karlEnabled) return;
    let cancelled = false;
    void fetchKarlAssists()
      .then((assists) => {
        if (!cancelled && assists) patch({ karlAssists: assists });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [karlEnabled, patch]);

  const candidates = useMemo(
    () => buildKarlCandidates(occurrences, supportedRuleIds),
    [occurrences, supportedRuleIds]
  );
  const candidatesByRule = useMemo(() => {
    const byRule = new Map<string, number>();
    candidates.forEach(({ finding }) => {
      byRule.set(finding.ruleId, (byRule.get(finding.ruleId) || 0) + 1);
    });
    return byRule;
  }, [candidates]);

  const overviewRows = useMemo(
    () =>
      buildKarlOverview(occurrences, karlVerdicts, (ruleId) =>
        ruleNameFor(ruleId, rules, catalogSections, lang)
      ),
    [occurrences, karlVerdicts, rules, catalogSections, lang]
  );

  const surePlan = useMemo(
    () => buildKarlSureApplyPlan(occurrences, karlVerdicts, karlReviewed, karlAccepted),
    [occurrences, karlVerdicts, karlReviewed, karlAccepted]
  );

  const visibleCount = useMemo(
    () => countKarlVisibleCases(occurrences, karlVerdicts, skipSure),
    [occurrences, karlVerdicts, skipSure]
  );

  const appliedEntries = useMemo(
    () =>
      Object.keys(karlAccepted)
        .map(Number)
        .sort((left, right) => left - right)
        .flatMap((index) => {
          const occurrence = occurrences[index];
          const verdict = karlVerdicts[karlCaseKey(index)];
          if (!occurrence) return [];
          const label =
            verdict && verdict.decision === "change"
              ? `${occurrence.word ?? ""} → ${verdict.word}`
              : String(occurrence.word ?? "");
          return [{ index, label }];
        }),
    [karlAccepted, occurrences, karlVerdicts]
  );

  const openConsent = (accountRequired: boolean) => {
    setConsentFailure(accountRequired ? "account" : null);
    setConsentOpen(true);
  };

  const advanceAfter = (index: number, verdicts = useStore.getState().karlVerdicts) => {
    const next = nextKarlVisibleIndex(occurrences, verdicts, skipSure, index + 1);
    patch({ currentOccurrenceIndex: next === -1 ? occurrences.length : next });
  };

  const askKarl = async (index: number) => {
    const occurrence = useStore.getState().occurrences[index];
    if (!karlEnabled || !occurrence) return;
    if (!supportedRuleIds.has(occurrence.ruleId)) {
      setCaseStatus(index, { status: "unsupported" });
      return;
    }

    const finding = buildKarlFinding(occurrence);
    if (!finding) {
      setCaseStatus(index, { status: "unavailable" });
      return;
    }

    const generation = scanGeneration.current;
    setCaseStatus(index, { status: "loading" });
    try {
      const result = await fetchKarlVerdict(finding);
      if (generation !== scanGeneration.current) return;
      patch({
        karlVerdicts: { ...useStore.getState().karlVerdicts, [karlCaseKey(index)]: result.verdict },
        karlCaseStatuses: withoutKey(useStore.getState().karlCaseStatuses, karlCaseKey(index)),
        ...(result.assists ? { karlAssists: result.assists } : {}),
      });
    } catch (error) {
      if (generation !== scanGeneration.current) return;
      const outcome = classifyKarlError(error);
      if (outcome.kind === "consent") {
        setCaseStatus(index, null);
        pendingConsent.current = { kind: "ask", index };
        openConsent(outcome.accountRequired);
        return;
      }
      if (outcome.kind === "exhausted") {
        setCaseStatus(index, { status: "exhausted", reason: outcome.reason });
        refreshAssists();
        return;
      }
      setCaseStatus(index, {
        status: outcome.kind === "unsupported" ? "unsupported" : "unavailable",
      });
    }
  };

  const acceptConsent = async () => {
    setConsentSubmitting(true);
    setConsentFailure(null);
    try {
      await acceptKarlConsent();
    } catch (error) {
      const outcome = classifyKarlError(error);
      setConsentSubmitting(false);
      setConsentFailure(outcome.kind === "consent" && outcome.accountRequired ? "account" : "save");
      return;
    }

    setConsentSubmitting(false);
    setConsentOpen(false);
    const pending = pendingConsent.current;
    pendingConsent.current = null;
    if (pending?.kind === "ask") {
      await askKarl(pending.index);
      return;
    }
    if (pending?.kind === "bulk") {
      await confirmBulk();
    }
  };

  const startBulk = async (scope: KarlBulkScope) => {
    if (!karlEnabled) return;
    const items = candidates.filter(
      (candidate) => scope.kind === "all" || candidate.finding.ruleId === scope.ruleId
    );
    if (items.length === 0) return;

    const generation = ++bulkGeneration.current;
    setBulk({ scope, phase: "estimating", items, estimate: null });
    setBulkSummary(null);
    try {
      const estimate = await estimateKarlVerdicts(items.map((item) => item.finding));
      if (generation !== bulkGeneration.current) return;
      setBulk({ scope, phase: "ready", items, estimate });
      if (estimate.assists) patch({ karlAssists: estimate.assists });
    } catch {
      if (generation !== bulkGeneration.current) return;
      setBulk({ scope, phase: "error", items, estimate: null });
    }
  };

  const cancelBulk = () => {
    bulkGeneration.current += 1;
    setBulk(null);
    setBulkSummary(null);
  };

  const confirmBulk = async () => {
    const current = bulk;
    if (!current || current.phase !== "ready" || !current.estimate) return;

    const generation = bulkGeneration.current;
    setBulk({ ...current, phase: "running" });
    try {
      const limit =
        current.estimate.canAnswerAll || current.estimate.chargeable === 0
          ? undefined
          : (current.estimate.maximum ?? 0);
      const result = await answerKarlVerdicts(
        current.items.map((item) => item.finding),
        limit
      );
      if (generation !== bulkGeneration.current) return;

      const nextVerdicts = { ...useStore.getState().karlVerdicts };
      const nextStatuses = { ...useStore.getState().karlCaseStatuses };
      result.results.forEach((entry) => {
        const item = current.items[entry.index];
        if (!item) return;
        const key = karlCaseKey(item.index);
        if (entry.verdict && (entry.status === "answered" || entry.status === "cached")) {
          nextVerdicts[key] = entry.verdict;
          delete nextStatuses[key];
          return;
        }
        delete nextVerdicts[key];
        nextStatuses[key] = caseStatusFromBatchResult(entry);
      });

      patch({
        karlVerdicts: nextVerdicts,
        karlCaseStatuses: nextStatuses,
        ...(result.assists ? { karlAssists: result.assists } : {}),
      });
      setBulkSummary(result.summary);
      setBulk(null);
      if (!result.assists) refreshAssists();
    } catch (error) {
      if (generation !== bulkGeneration.current) return;
      const outcome = classifyKarlError(error);
      if (outcome.kind === "consent") {
        setBulk({ ...current, phase: "ready" });
        pendingConsent.current = { kind: "bulk" };
        openConsent(outcome.accountRequired);
        return;
      }

      const nextStatuses = { ...useStore.getState().karlCaseStatuses };
      current.items.forEach((item) => {
        nextStatuses[karlCaseKey(item.index)] =
          outcome.kind === "exhausted"
            ? { status: "exhausted", reason: outcome.reason }
            : { status: "unavailable" };
      });
      patch({ karlCaseStatuses: nextStatuses });
      setBulk({ ...current, phase: "error" });
      if (outcome.kind === "exhausted") refreshAssists();
    }
  };

  const applySureVerdicts = async () => {
    if (applying || surePlan.findings === 0) return;

    setApplying(true);
    let nextWorkingText = useStore.getState().workingSelectionText;
    let nextEdits = useStore.getState().occurrenceEdits;
    let nextAccepted = { ...useStore.getState().karlAccepted };
    const appliedIndexes: number[] = [];

    try {
      for (const entry of surePlan.entries) {
        const verdict = useStore.getState().karlVerdicts[karlCaseKey(entry.index)];
        if (!verdict) continue;

        if (verdict.decision === "change") {
          const edit = replaceOccurrenceText({
            workingText: nextWorkingText,
            occurrences,
            edits: nextEdits,
            index: entry.index,
            replacement: verdict.word,
          });
          await replaceOccurrenceInSelection(
            edit.currentWord,
            verdict.word,
            edit.occurrenceIndex,
            t.occurrenceNotFound
          );
          nextWorkingText = edit.workingText;
          nextEdits = edit.edits;
        }

        nextAccepted = { ...nextAccepted, [karlCaseKey(entry.index)]: verdict.decision };
        appliedIndexes.push(entry.index);
        patch({
          workingSelectionText: nextWorkingText,
          occurrenceEdits: nextEdits,
          karlAccepted: nextAccepted,
        });
      }
    } catch (error) {
      presentReviewError(error);
    } finally {
      /* A failed Office edit stops the batch, but the changes already written
         are real: record them and move past the current card. */
      recordDecisions(
        appliedIndexes.map((index) =>
          buildKarlDecision(
            occurrences[index],
            useStore.getState().karlVerdicts[karlCaseKey(index)],
            "accepted_with_karl"
          )
        )
      );
      if (appliedIndexes.includes(currentIndex)) advanceAfter(currentIndex);
      setApplying(false);
    }
  };

  const undoKarlAcceptance = async (index: number) => {
    if (applying) return;
    setApplying(true);
    const key = karlCaseKey(index);
    const occurrence = occurrences[index];
    const decision = useStore.getState().karlAccepted[key];

    try {
      if (decision === "change" && occurrence) {
        const edit = undoOccurrenceText({
          workingText: useStore.getState().workingSelectionText,
          occurrences,
          edits: useStore.getState().occurrenceEdits,
          index,
        });
        await replaceOccurrenceInSelection(
          edit.currentWord,
          String(occurrence.word ?? ""),
          edit.occurrenceIndex,
          t.occurrenceNotFound
        );
        patch({ workingSelectionText: edit.workingText, occurrenceEdits: edit.edits });
      }

      /* Bring the finding back into review: undo is not a dead end, and the
         card must be reachable even when the confidence filter hid it. */
      setSkipSure(false);
      patch({
        karlAccepted: withoutKey(useStore.getState().karlAccepted, key),
        karlReviewed: { ...useStore.getState().karlReviewed, [key]: true },
        currentOccurrenceIndex: index,
      });
      recordDecisions([
        buildKarlDecision(occurrence, useStore.getState().karlVerdicts[key], "undone"),
      ]);
    } catch (error) {
      presentReviewError(error);
    } finally {
      setApplying(false);
    }
  };

  const changeSkipSure = (value: boolean) => {
    setSkipSure(value);
    if (occurrences.length === 0) return;

    const state = useStore.getState();
    const atEnd = state.currentOccurrenceIndex >= state.occurrences.length;
    if (value) {
      const currentVerdict = state.karlVerdicts[karlCaseKey(state.currentOccurrenceIndex)];
      const hidden =
        state.currentOccurrenceIndex < 0 || atEnd || currentVerdict?.confidence === "sure";
      if (hidden) {
        const from = atEnd ? 0 : Math.max(0, state.currentOccurrenceIndex);
        patch({
          currentOccurrenceIndex: nextKarlVisibleIndex(
            state.occurrences,
            state.karlVerdicts,
            true,
            from
          ),
        });
      }
      return;
    }

    if (state.currentOccurrenceIndex < 0 || atEnd) {
      patch({
        currentOccurrenceIndex: nextKarlVisibleIndex(
          state.occurrences,
          state.karlVerdicts,
          false,
          0
        ),
      });
    }
  };

  const scan = async () => {
    setScanning(true);
    setMessage("");
    setIsError(false);
    setShowUpgrade(false);
    try {
      const confusionRuleIds = getSelectedConfusionRuleIds(rules, selectedConfusionRuleIds);
      if (confusionRuleIds.length === 0) throw new Error(t.noConfusionRulesSelected);

      const selectedText = await readSelectionText();
      if (!selectedText?.trim()) throw new Error(t.emptySelection);

      const found = await scanGreekEditorConfusion(token, selectedText, confusionRuleIds);
      scanGeneration.current += 1;
      bulkGeneration.current += 1;
      setSkipSure(false);
      setBulk(null);
      setBulkSummary(null);
      setApplying(false);
      pendingConsent.current = null;
      setConsentOpen(false);

      if (found.length === 0) {
        setMessage(t.noIssues);
        patch({
          occurrences: [],
          currentOccurrenceIndex: -1,
          originalSelectionText: selectedText,
          workingSelectionText: selectedText,
          occurrenceEdits: {},
          karlVerdicts: {},
          karlCaseStatuses: {},
          karlReviewed: {},
          karlAccepted: {},
        });
      } else {
        patch({
          originalSelectionText: selectedText,
          workingSelectionText: selectedText,
          occurrences: found,
          currentOccurrenceIndex: 0,
          occurrenceEdits: {},
          karlVerdicts: {},
          karlCaseStatuses: {},
          karlReviewed: {},
          karlAccepted: {},
        });
      }
    } catch (err) {
      presentError(err);
    } finally {
      setScanning(false);
    }
  };

  const skip = () => {
    const occurrence = occurrences[currentIndex];
    if (!occurrence) return;
    const key = karlCaseKey(currentIndex);
    patch({ karlReviewed: { ...karlReviewed, [key]: true } });
    recordDecisions([buildKarlDecision(occurrence, karlVerdicts[key], "kept")]);
    advanceAfter(currentIndex);
  };

  const handleCancel = () => {
    patch({
      occurrences: [],
      currentOccurrenceIndex: -1,
      occurrenceEdits: {},
      karlVerdicts: {},
      karlCaseStatuses: {},
      karlReviewed: {},
      karlAccepted: {},
    });
    setBulk(null);
    setBulkSummary(null);
    setMessage("");
    setIsError(false);
  };

  const accept = async () => {
    const occurrence = occurrences[currentIndex];
    if (!occurrence) return;
    setBusy(true);
    try {
      const replacement = String(occurrence.replacement ?? "");
      if (!replacement) throw new OccurrenceChangedError();
      const edit = replaceOccurrenceText({
        workingText,
        occurrences,
        edits: occurrenceEdits,
        index: currentIndex,
        replacement,
      });
      await replaceOccurrenceInSelection(
        edit.currentWord,
        replacement,
        edit.occurrenceIndex,
        t.occurrenceNotFound
      );

      const key = karlCaseKey(currentIndex);
      patch({
        workingSelectionText: edit.workingText,
        occurrenceEdits: edit.edits,
        karlReviewed: { ...karlReviewed, [key]: true },
      });
      recordDecisions([buildKarlDecision(occurrence, karlVerdicts[key], "changed")]);
      advanceAfter(currentIndex);
    } catch (err) {
      presentReviewError(err);
    } finally {
      setBusy(false);
    }
  };

  const openReport = () => patch({ reportModalOpen: true, reportModalTarget: "selection" });

  const hasActive = currentIndex >= 0 && currentIndex < occurrences.length;
  const occ: Occurrence | null = hasActive ? occurrences[currentIndex] : null;
  const friendly = occ ? getFriendlyRule(occ.ruleId, rules, catalogSections, lang) : null;
  const finished = occurrences.length > 0 && currentIndex >= occurrences.length;
  const filteredEmpty = karlEnabled && skipSure && currentIndex < 0 && occurrences.length > 0;
  const key = hasActive ? karlCaseKey(currentIndex) : "";
  const verdict = hasActive ? (karlVerdicts[key] ?? null) : null;
  const caseStatus = hasActive ? (karlCaseStatuses[key] ?? null) : null;
  const accepted = hasActive ? Boolean(karlAccepted[key]) : false;
  const ruleFindingCount = occ ? candidatesByRule.get(occ.ruleId) || 0 : 0;
  const karlBusy = applying || busy || bulk?.phase === "running";

  const caseStatusText = (() => {
    if (!caseStatus) return "";
    switch (caseStatus.status) {
      case "loading":
        return t.karlAsking;
      case "unsupported":
        return t.karlUnsupported;
      case "unavailable":
        return t.karlUnavailable;
      case "limited":
        return t.karlBulkLimitStopped;
      case "exhausted":
        return caseStatus.reason === "today"
          ? t.karlAssistsExhaustedToday
          : caseStatus.reason === "cycle"
            ? t.karlAssistsExhaustedCycle
            : t.karlAssistsExhausted;
    }
    return "";
  })();

  const caseAvatarState: KarlAvatarState = accepted
    ? "done"
    : caseStatus
      ? caseStatus.status === "loading"
        ? "thinking"
        : "cant"
      : verdict
        ? karlAvatarStateForConfidence(verdict.confidence)
        : "idle";

  const bulkScopeTitle = (() => {
    if (!bulk) return "";
    if (bulk.scope.kind === "all") return t.karlBulkScopeAll;
    return ruleNameFor(bulk.scope.ruleId, rules, catalogSections, lang);
  })();

  const bulkConfirmLabel = (() => {
    if (!bulk?.estimate) return "";
    if (bulk.estimate.chargeable === 0) return t.karlBulkShowAnswers;
    if (bulk.estimate.canAnswerAll) {
      return format(t.karlBulkAnswerAll, {
        count: numberFormat.format(bulk.estimate.chargeable),
      });
    }
    return format(t.karlBulkAnswerFirst, {
      count: numberFormat.format(bulk.estimate.maximum ?? 0),
    });
  })();

  const bulkCanConfirm = (() => {
    if (!bulk?.estimate) return false;
    if (bulk.estimate.chargeable === 0) return true;
    return bulk.estimate.maximum === null || bulk.estimate.maximum > 0;
  })();

  const hasFindings = useStore(hasSelectionFindings);
  const { controlsExpanded, toggleControls, findingsRef } = useFindingsAutoCollapse(hasFindings);

  return (
    <div className="panel-section">
      <CollapsibleSection title={t.scanBtn} expanded={controlsExpanded} onToggle={toggleControls}>
        <MessageBar intent="info" className="status-card">
          <MessageBarBody>{t.confusionAssistantInfo}</MessageBarBody>
        </MessageBar>
        <Button
          appearance="primary"
          onClick={scan}
          disabled={scanning || busy || applying || bulk !== null}
        >
          {t.scanBtn}
        </Button>
      </CollapsibleSection>

      {scanning ? <Spinner size="small" label={t.statusScanning} /> : null}

      {message ? (
        isError ? (
          <MessageBar intent="error">
            <MessageBarBody>
              {message}
              {showUpgrade ? <UpgradePlanButton label={t.quotaExceededUpgrade} /> : null}
            </MessageBarBody>
          </MessageBar>
        ) : (
          <Text>{message}</Text>
        )
      ) : null}

      <div ref={findingsRef}>
        {karlEnabled && occurrences.length > 0 ? (
          <>
            <KarlOverview
              rows={overviewRows}
              assists={karlAssists}
              sureFindings={surePlan.findings}
              sureChanges={surePlan.changes}
              skipSure={skipSure}
              onSkipSureChange={changeSkipSure}
              onAskRule={
                occ && ruleFindingCount > 0
                  ? () => void startBulk({ kind: "rule", ruleId: occ.ruleId })
                  : undefined
              }
              ruleCount={ruleFindingCount}
              onAskAll={() => void startBulk({ kind: "all" })}
              allCount={candidates.length}
              applyDisabled={karlBusy || bulk !== null}
              applying={karlBusy}
              onApplySure={() => void applySureVerdicts()}
              applied={appliedEntries}
              onUndo={(index) => void undoKarlAcceptance(index)}
            />

            {bulk ? (
              <div
                className="karl-bulk"
                data-karl-bulk-panel
                role="region"
                aria-label={t.karlBulkPanelLabel}
                aria-live="polite"
              >
                <div className="karl-bulk__header">
                  <KarlAvatar
                    compact
                    state={
                      bulk.phase === "error" ? "cant" : bulk.phase === "ready" ? "idle" : "thinking"
                    }
                  />
                  <Text size={200} className="karl-bulk__title">
                    {format(t.karlBulkTitle, { scope: bulkScopeTitle })}
                  </Text>
                </div>

                {bulk.phase === "estimating" ? (
                  <Text size={200}>{t.karlBulkEstimating}</Text>
                ) : bulk.phase === "running" ? (
                  <Text size={200}>{t.karlAsking}</Text>
                ) : bulk.phase === "ready" && bulk.estimate ? (
                  <>
                    <Text size={200} data-karl-bulk-estimate>
                      {formatKarlEstimate(bulk.estimate, t, (value) => numberFormat.format(value))}
                    </Text>
                    {bulk.estimate.cached > 0 ? (
                      <Text size={200}>
                        {format(t.karlBulkCachedNote, {
                          count: numberFormat.format(bulk.estimate.cached),
                        })}
                      </Text>
                    ) : null}
                    {!bulk.estimate.canAnswerAll ? (
                      <Text size={200}>
                        {format(t.karlBulkShortfall, {
                          count: numberFormat.format(bulk.estimate.chargeable),
                          maximum: numberFormat.format(bulk.estimate.maximum ?? 0),
                        })}
                      </Text>
                    ) : null}
                    <div className="karl-bulk__actions">
                      {bulkCanConfirm ? (
                        <Button
                          appearance="primary"
                          size="small"
                          onClick={() => void confirmBulk()}
                          disabled={karlBusy}
                        >
                          {bulkConfirmLabel}
                        </Button>
                      ) : null}
                      <Button appearance="secondary" size="small" onClick={cancelBulk}>
                        {t.karlBulkCancel}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Text size={200}>{t.karlUnavailable}</Text>
                    <div className="karl-bulk__actions">
                      <Button appearance="secondary" size="small" onClick={cancelBulk}>
                        {t.karlBulkCancel}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {bulkSummary ? (
              <Text size={200} data-karl-bulk-summary>
                {formatKarlBatchSummary(bulkSummary, t, (value) => numberFormat.format(value))}
              </Text>
            ) : null}
          </>
        ) : null}

        {filteredEmpty ? (
          <div className="karl-filtered-empty" data-karl-filtered-empty>
            <Text>{t.karlFilteredEmpty}</Text>
            <Button appearance="secondary" size="small" onClick={() => changeSkipSure(false)}>
              {t.karlOverviewClearFilter}
            </Button>
          </div>
        ) : null}

        {finished && !message ? (
          <div className="karl-finished">
            <KarlAvatar compact state="done" />
            <Text>{t.statusDone}</Text>
            {skipSure && visibleCount > 0 ? (
              <Button appearance="secondary" size="small" onClick={() => changeSkipSure(false)}>
                {t.karlOverviewClearFilter}
              </Button>
            ) : null}
          </div>
        ) : null}

        {!hasActive && !scanning && !message && occurrences.length === 0 ? (
          <Text>{t.promptSelection}</Text>
        ) : null}

        {occ && friendly ? (
          <SuggestionCard
            counterText={format(t.confusionCounter, {
              current: currentIndex + 1,
              total: occurrences.length,
            })}
            progressPercent={((currentIndex + 1) / occurrences.length) * 100}
            sentence={String(occ.sentenceContext || workingText || "")}
            before={String(occ.word ?? "")}
            after={String(occ.replacement ?? "")}
            ruleName={friendly.name}
            ruleTooltip={occ.explanation || friendly.desc}
            onAccept={accept}
            onSkip={skip}
            onCancel={handleCancel}
            onReport={openReport}
            busy={busy}
            extraContent={
              karlEnabled ? (
                <div className="karl-case" data-karl-case>
                  <div className="karl-case__actions">
                    <KarlAvatar compact state={caseAvatarState} />
                    <Button
                      appearance="secondary"
                      size="small"
                      onClick={() => void askKarl(currentIndex)}
                      disabled={karlBusy || caseStatus?.status === "loading"}
                    >
                      {t.karlAsk}
                    </Button>
                    {accepted ? (
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={() => void undoKarlAcceptance(currentIndex)}
                        disabled={applying}
                      >
                        {t.karlUndo}
                      </Button>
                    ) : null}
                  </div>

                  {caseStatus && verdict === null ? (
                    <Text
                      size={200}
                      className="karl-case__message"
                      data-karl-message={caseStatus.status}
                    >
                      {caseStatusText}
                    </Text>
                  ) : null}

                  {verdict ? <KarlVerdictBadge verdict={verdict} accepted={accepted} /> : null}
                </div>
              ) : null
            }
          />
        ) : null}
      </div>

      <KarlConsentModal
        open={consentOpen}
        submitting={consentSubmitting}
        failure={consentFailure}
        onClose={() => {
          setConsentOpen(false);
          setConsentFailure(null);
          pendingConsent.current = null;
        }}
        onAccept={() => void acceptConsent()}
        onGoToAccount={() => {
          setConsentOpen(false);
          pendingConsent.current = null;
          useStore.getState().setActiveTab("settings");
        }}
      />
    </div>
  );
}
